/**
 * SuggestedForYou — the "likely needed soon" section, built as a UI STUB
 * (T12.1.5.1.2).
 *
 * This is the slot the ranking algorithm (T12.5) plugs into. We build the full
 * interface now — heading, card layout, an "+ Add" action per row — driven from
 * a `suggestions` prop. Until T12.5 lands, the caller passes an EMPTY array, so
 * the section renders its quiet "coming soon" placeholder. When the ranking is
 * built, it fills `suggestions` and the cards light up with no further UI work.
 *
 * The insertion point is the `suggestions` prop + `onAdd` callback — that is the
 * contract T12.5 fulfils. No ranking, scoring, or data source lives here.
 *
 * All styles flow from tokens (CP5).
 */

import type { CSSProperties } from 'react';
import { Button } from '../../components';
import * as s from './planningModeStyles';

/** One suggested item. T12.5 fills these; the stub renders from an empty list. */
export interface Suggestion {
  /** HouseholdProduct id to add to the list when the user taps Add. */
  householdproductId: string;
  name: string;
  emoji?: string;
  /** Why it's suggested — e.g. "Last bought 4 weeks ago · on sale at Costco". */
  reason?: string;
}

export interface SuggestedForYouProps {
  suggestions: Suggestion[];
  onAdd: (suggestion: Suggestion) => void;
}

const placeholderStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
  backgroundColor: 'var(--color-bg-raised)',
  borderWidth: '0.0625rem',
  borderStyle: 'dashed',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-4)',
  textAlign: 'center',
};

export function SuggestedForYou({
  suggestions,
  onAdd,
}: SuggestedForYouProps): JSX.Element {
  return (
    <section data-testid="plan-suggested">
      <h3 style={s.sectionHeadStyle}>Suggested for you · likely needed soon</h3>
      {suggestions.length === 0 ? (
        <div style={placeholderStyle} data-testid="plan-suggested-empty">
          Suggestions appear here as we learn what your household buys.
        </div>
      ) : (
        suggestions.map((sug) => (
          <div
            key={sug.householdproductId}
            style={s.cardStyle}
            data-testid={`plan-suggested-${sug.householdproductId}`}
          >
            <div style={{ ...s.rowStyle, cursor: 'default' }}>
              <span style={s.thumbStyle} aria-hidden="true">
                {sug.emoji ?? '🛒'}
              </span>
              <span style={s.metaStyle}>
                <span style={s.nameStyle}>{sug.name}</span>
                {sug.reason && (
                  <span style={s.brandLineStyle}>{sug.reason}</span>
                )}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAdd(sug)}
                data-testid={`plan-suggested-${sug.householdproductId}-add`}
              >
                + Add
              </Button>
            </div>
          </div>
        ))
      )}
    </section>
  );
}
